import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchAllProjects, setSelectedProject } from '../../../store/slices/projectsSlice';
import { Project } from './types';
import { FaFolder, FaPlus, FaEdit, FaTrash, FaCog } from 'react-icons/fa';
import { HiChevronDown } from 'react-icons/hi';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";

interface ProjectSelectorProps {
  selectedProjectId?: string;
  onProjectChange: (projectId: string | undefined) => void;
  showManageButton?: boolean;
  onManageClick?: () => void;
  onCreateClick?: () => void;
  required?: boolean; // Make project selection required or optional
}


export interface ProjectSelectorHandle {
  focus: () => void;
  open: () => void;
}

const ProjectSelector = React.forwardRef<ProjectSelectorHandle, ProjectSelectorProps>(({
  selectedProjectId,
  onProjectChange,
  showManageButton = true,
  onManageClick,
  onCreateClick,
  required = false, // Default to optional
}, ref) => {
  const dispatch = useAppDispatch();
  const { projects, isLoading } = useAppSelector((state) => state.projects);
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  React.useImperativeHandle(ref, () => ({
    focus: () => {
      if (buttonRef.current) {
        buttonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        buttonRef.current.focus();
      }
    },
    open: () => {
      setIsOpen(true);
      if (buttonRef.current) {
        buttonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        buttonRef.current.focus();
      }
    }
  }));

  useEffect(() => {
    dispatch(fetchAllProjects());
  }, [dispatch]);

  // Auto-select default project if no project is selected and required is true
  useEffect(() => {
    if (!selectedProjectId && projects.length > 0) {
      const defaultProject = projects.find(p => p.isDefault);
      if (defaultProject) {
        onProjectChange(defaultProject.id);
      }
    }
  }, [projects, selectedProjectId, onProjectChange]);

  const handleProjectSelect = (projectId: string | undefined) => {
    onProjectChange(projectId);
    setIsOpen(false);
    if (projectId) {
      dispatch(setSelectedProject(projectId));
    } else {
      dispatch(setSelectedProject(null));
    }
  };

  const selectedProject = projects.find(p => p.id === selectedProjectId);

  const getProjectColor = (color?: string) => {
    if (!color) return 'bg-orange-100 text-orange-600';
    // Parse hex color and determine if it's light or dark
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? 'text-gray-700' : 'text-white';
  };

  return (
    <div className="relative">
      <div className="space-y-2">
        <label className="block text-lg text-gray-700 font-normal">
          Focus Area {required && <span className="text-orange-600">*</span>}
        </label>
        <div className="relative">
          <button
            ref={buttonRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            className={`w-full h-12 px-4 bg-white border rounded-xl text-left flex items-center justify-between transition-all duration-200 ${required && !selectedProjectId
              ? 'border-orange-400 hover:border-orange-500'
              : 'border-gray-200/60 hover:border-orange-300/50'
              }`}
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {selectedProject ? (
                <>
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: selectedProject.color || '#f97316' }}
                  />
                  <span className="text-gray-900 font-normal truncate">
                    {selectedProject.name}
                  </span>
                </>
              ) : (
                <span className="text-gray-400 font-normal">Select</span>
              )}
            </div>
            <HiChevronDown className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
          {required && !selectedProjectId && (
            <p className="mt-1 text-xs text-orange-600">Please select a project to continue</p>
          )}

          {isOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setIsOpen(false)}
              />
              <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200/60 rounded-xl shadow-lg overflow-hidden">
                <div className="p-2">
                  <div className="max-h-60 overflow-y-auto">
                    {isLoading ? (
                      <div className="px-3 py-4 text-center text-sm text-gray-500">Loading projects...</div>
                    ) : (
                      projects.map((project) => (
                        <button
                          key={project.id}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleProjectSelect(project.id);
                          }}
                          className={`w-full px-3 py-2 text-left text-sm rounded-lg flex items-center gap-2 transition-colors outline-none focus:outline-none focus:ring-0 border-none ${selectedProjectId === project.id
                            ? 'bg-orange-50 text-orange-600'
                            : 'text-gray-700 hover:bg-orange-50/40'
                            }`}
                        >
                          <div
                            className={`w-3 h-3 rounded-full flex-shrink-0`}
                            style={{ backgroundColor: project.color || '#f97316' }}
                          />
                          <span className="truncate flex-1">
                            {project.name}
                          </span>
                        </button>
                      ))
                    )}
                  </div>

                  {showManageButton && (
                    <div className="border-t border-gray-200/60 mt-2 pt-2 flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsOpen(false);
                          if (onCreateClick) onCreateClick();
                        }}
                        className="flex-1 px-3 py-2 text-left text-sm text-orange-600 hover:bg-orange-50/40 rounded-lg flex items-center gap-2 transition-colors"
                      >
                        <FaPlus className="text-xs" />
                        <span>Create Focus Area</span>
                      </button>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsOpen(false);
                                if (onManageClick) onManageClick();
                              }}
                              className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50/40 rounded-lg transition-colors"
                            >
                              <FaCog className="text-sm" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Manage Projects</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

ProjectSelector.displayName = 'ProjectSelector';

export default ProjectSelector;

